/**
 * RP96 / RP97 (D35 C, ADM-A7, ADM-R8) — Paramètres › Assistant IA (administrateur).
 *
 * - Réglage en 3 positions : Activée / Fournisseurs en Europe uniquement / Désactivée
 *   (PUT /api/admin/ia) ;
 * - fonctions IA avec leur disponibilité dans la position choisie (grisées + raison) ;
 * - « Sous-traitants IA » : fournisseurs, usages, données envoyées, hébergement
 *   (GET /api/admin/ia/sous-traitants). Un hébergement non vérifié est affiché
 *   « Non vérifié » : aucun engagement européen n'est inventé.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, ShieldCheck, Prohibit, Globe, CheckCircle, MinusCircle } from '@phosphor-icons/react';
import { rafraichirIA } from '../../hooks/useIA';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  {
    mode: 'active',
    libelle: 'Activée',
    aide: "Toutes les fonctions de l'assistant sont disponibles.",
    icon: Sparkle,
  },
  {
    mode: 'ue_uniquement',
    libelle: 'Fournisseurs en Europe uniquement',
    aide: "Seuls les fournisseurs hébergés en Europe (vérifié) sont utilisés. Les autres fonctions sont grisées, avec une explication.",
    icon: Globe,
  },
  {
    mode: 'desactivee',
    libelle: 'Désactivée',
    aide: "Aucune donnée n'est envoyée à un fournisseur d'IA. Les boutons de l'assistant sont grisés.",
    icon: Prohibit,
  },
];

export default function IASettings() {
  const [reglage, setReglage] = useState(null);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [saving, setSaving] = useState(false);

  const charger = async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ia`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/ia/sous-traitants`, { withCredentials: true }),
      ]);
      setReglage(r.data);
      setSousTraitants(s.data?.sous_traitants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger le réglage');
    }
  };

  useEffect(() => { charger(); }, []);

  const choisir = async (mode) => {
    if (!reglage || mode === reglage.mode || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/ia`, { mode }, { withCredentials: true });
      toast.success(data.message || 'Réglage enregistré');
      await charger();
      rafraichirIA();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Le réglage n'a pas été enregistré");
    } finally {
      setSaving(false);
    }
  };

  if (!reglage) {
    return <div className="p-6 text-sm text-[#6B6B6B]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="ia-settings">
      <section className="border border-[#E0E0DB] bg-white p-5">
        <h2 className="text-lg font-semibold text-[#2C2C2C] mb-1">Assistant IA du cabinet</h2>
        <p className="text-sm text-[#6B6B6B] mb-4">
          Choisissez si l'assistant peut envoyer des données (messages, vocaux, photos, documents) à des fournisseurs d'IA.
        </p>
        <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Réglage de l'assistant IA">
          {POSITIONS.map((p) => {
            const Icon = p.icon;
            const actif = reglage.mode === p.mode;
            return (
              <button
                key={p.mode}
                type="button"
                role="radio"
                aria-checked={actif}
                disabled={saving}
                onClick={() => choisir(p.mode)}
                className={`text-left border p-4 transition-colors ${actif ? 'border-[#2C2C2C] bg-[#F5F5F3]' : 'border-[#E0E0DB] hover:border-[#2C2C2C]'}`}
                data-testid={`ia-mode-${p.mode}`}
              >
                <div className="flex items-center gap-2 font-medium text-[#2C2C2C]">
                  <Icon size={18} weight={actif ? 'fill' : 'regular'} />
                  {p.libelle}
                  {actif && <CheckCircle size={16} weight="fill" className="ml-auto text-[#2D6A4F]" />}
                </div>
                <p className="text-xs text-[#6B6B6B] mt-2">{p.aide}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="border border-[#E0E0DB] bg-white p-5">
        <h3 className="font-semibold text-[#2C2C2C] mb-3">Fonctions de l'assistant</h3>
        <ul className="divide-y divide-[#E0E0DB]">
          {(reglage.fonctions_detail || []).map((f) => (
            <li key={f.cle} className={`py-2 flex items-start gap-3 ${f.disponible ? '' : 'opacity-60'}`} data-testid={`ia-fonction-${f.cle}`}>
              {f.disponible
                ? <CheckCircle size={18} weight="fill" className="text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                : <MinusCircle size={18} className="text-[#9A9A9A] mt-0.5 flex-shrink-0" />}
              <div>
                <p className="text-sm text-[#2C2C2C]">{f.libelle}</p>
                {!f.disponible && <p className="text-xs text-[#6B6B6B]">{f.raison}</p>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="border border-[#E0E0DB] bg-white p-5" id="sous-traitants-ia" data-testid="ia-sous-traitants">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-[#1E3A5F]" />
          <h3 className="font-semibold text-[#2C2C2C]">Sous-traitants IA</h3>
        </div>
        <p className="text-xs text-[#6B6B6B] mb-4">
          Liste à communiquer à vos clients (art. 28 du RGPD). « Non vérifié » : la localisation des serveurs n'a pas été confirmée par contrat ; elle n'est donc pas présentée comme européenne.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#6B6B6B] border-b border-[#E0E0DB]">
                <th className="py-2 pr-3">Fournisseur</th>
                <th className="py-2 pr-3">Usages et données envoyées</th>
                <th className="py-2 pr-3">Hébergement</th>
                <th className="py-2 pr-3">Europe</th>
                <th className="py-2">Utilisé actuellement</th>
              </tr>
            </thead>
            <tbody>
              {sousTraitants.map((s) => (
                <tr key={s.cle} className="border-b border-[#F0F0EC] align-top" data-testid={`sous-traitant-${s.cle}`}>
                  <td className="py-2 pr-3 font-medium text-[#2C2C2C]">
                    {s.nom}
                    <div className="text-xs font-normal text-[#9A9A9A]">Siège : {s.pays_siege}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <ul className="space-y-1">
                      {s.usages.map((u) => (
                        <li key={u.fonction}>
                          <span className="text-[#2C2C2C]">{u.libelle}</span>
                          <div className="text-xs text-[#6B6B6B]">{u.donnees}</div>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-2 pr-3 text-xs text-[#6B6B6B]">{s.hebergement}</td>
                  <td className="py-2 pr-3 text-xs">{s.europe}</td>
                  <td className="py-2 text-xs">{s.appele_dans_ce_mode ? 'Oui' : 'Non'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
